/* Slideshow v1.0.0
 * Provides a simple, extendable, overridable slideshow interface.
 */

/// <reference path="../../typings/jquery/jquery.d.ts" />

import $ = require("jquery");

function resetSlide(slide: JQuery) {
	slide.removeClass("current next previous");
}

class Slideshow {
	version: Array<number> = [1, 0, 0];

	slideshow: JQuery = null;
	nextControl = null;
	previousControl = null;
	timeoutID = null;

	cssAnimateSpeed: string = "0s";
	animateSpeed: number = 1500; // in milliseconds
	cycleSpeed: number = 7000; // in milliseconds
	running: boolean = false;
	userCanCycle: boolean = true;

	/**
	 * Creates a slideshow from the given element.
	 */
	constructor(slideshow: JQuery) {
		this.slideshow = slideshow;

		let controls = this.slideshow.children(".controls");
		this.nextControl = controls.find(".next");
		this.previousControl = controls.find(".previous");

		this.nextControl.click(this.next.bind(this));
		this.previousControl.click(this.previous.bind(this));

		this.resetSlides();
		this.makeCurrent(this.getCurrent());
	}

	/**
	 * Gets the list of slides that the slider is using.
	 */
	getSlides(): JQuery {
		return this.slideshow.children(".slides").children();
	}

	/**
	 * Resets the slideshow and ensures that the setup is sane.
	 */
	resetSlides(): void {
		let slides: JQuery = this.getSlides();

		this.cssAnimateSpeed = this.animateSpeed / 1000 + "s";

		for (let i = 0; i < slides.length; i++) {
			let slide: JQuery = $(slides.get(i));

			slide.removeClass("next previous current");
			slide.css("z-index", -1);
			slide.css("transition-duration", this.cssAnimateSpeed);
		}

		this.ensureSlides();

		this.getCurrent().css("z-index", 0);
	}

	/**
	 * Makes sures that current, next, and previous slides are properly tagged.
	 */
	ensureSlides(backwards?: boolean): void {
		let slides = this.getSlides();

		this.getCurrent();
		this.getPrevious(backwards);
		this.getNext(backwards);
	}

	/**
	 * Returns the current slide being shown
	 */
	getCurrent(): JQuery {
		let slides = this.getSlides();

		for (let i = 0; i < slides.length; i++) {
			let slide = $(slides.get(i));

			if (slide.hasClass("current")) {
				return slide;
			}
		}

		let current = $(slides.get(0));
		this.makeCurrent(current);

		return current;
	}

	/**
	 * Returns the slide after the current slide
	 */
	getNext(backwards?: boolean): JQuery {
		let slides = this.getSlides();

		for (let i = 0; i < slides.length; i++) {
			let slide = $(slides.get(i));

			if (slide.hasClass("next")) {
				return slide;
			}
		}

		let current = this.getCurrent();
		let next = current.next();

		if (!next.length) {
			next = $(slides.get(0));
		}

		if (!next.hasClass("previous")) {
			this.makeNext(next, backwards);
		}

		return next;
	}

	/**
	 * Returns the slide previous to the current slide
	 */
	getPrevious(backwards?: boolean): JQuery {
		let slides = this.getSlides();

		for (let i = 0; i < slides.length; i++) {
			let slide = $(slides.get(i));

			if (slide.hasClass("previous")) {
				return slide;
			}
		}

		let current = this.getCurrent();
		let previous = current.prev();

		if (!previous.length) {
			previous = $(slides.get(slides.length - 1));
		}

		if (!previous.hasClass("next")) {
			this.makePrevious(previous, backwards);
		}

		return previous;
	}

	/**
	 * Cycles to the next slide, calling a callback after the animation completes.
	 */
	cycleNext(then?: Function): void {
		let slides = this.getSlides();
		const self = this;

		if (slides.length > 2) {
			let previous = this.getPrevious();
			let current = this.getCurrent();
			let next = this.getNext();

			this.makePrevious(current);
			this.makeCurrent(next);
			this.makeNothing(previous);

			this.ensureSlides();
		} else {
			let current = this.getCurrent();
			let next = this.getNext();

			resetSlide(current);
			resetSlide(next);

			next.css("transition-duration", "0s");

			setTimeout(function() {
				current.addClass("previous");
				next.css("transition-duration", self.cssAnimateSpeed);
				next.addClass("current");

				self.ensureSlides();
			}, 16);
		}

		this.userCanCycle = false;
		setTimeout(function() {
			self.userCanCycle = true;

			if (then) {
				then();
			}
		}, this.animateSpeed + 50);
	}

	/**
	 * Cycles to the previous slide, calling a callback after the animation completes.
	 */
	cyclePrevious(then?: Function): void {
		let slides = this.getSlides();
		const self = this;

		if (slides.length > 2) {
			let previous = this.getPrevious();
			let current = this.getCurrent();
			let next = this.getNext();

			resetSlide(previous);
			resetSlide(current);
			resetSlide(next);

			this.makeCurrent(previous);
			this.makeNext(current, true);
			this.makeNothing(next);

			this.ensureSlides(true);
		} else {
			let current = this.getCurrent();
			let previous = this.getPrevious();

			resetSlide(current);

			previous.css("transition-duration", "0s");
			previous.addClass("previous");

			setTimeout(function() {
				previous.css("transition-duration", self.cssAnimateSpeed);

				current.addClass("next");
				previous.addClass("current");
				previous.removeClass("previous");

				// self.ensureSlides();
			}, 16);	
		}

		this.userCanCycle = false;
		setTimeout(function() {
			self.userCanCycle = true;

			if (then) {
				then();
			}
		}, this.animateSpeed + 50);
	}

	makeCurrent(slide: JQuery): void {
		resetSlide(slide);
		slide.addClass("current");
		slide.css("z-index", 0);
	}

	makePrevious(slide: JQuery, backwards?: boolean): void {
		resetSlide(slide);
		slide.addClass("previous");
		slide.css("z-index", backwards ? -2 : -1);
	}

	makeNext(slide: JQuery, backwards?: boolean): void {
		resetSlide(slide);
		slide.addClass("next");
		slide.css("z-index", backwards ? -1 : -2);
	}

	makeNothing(slide: JQuery): void {
		resetSlide(slide);
		slide.removeClass("current previous next");
		slide.css("z-index", -3);
	}

	/**
	 * Cycles to the next element if possible.
	 * Used for the 'next' control.
	 */
	next(): void {
		if (this.userCanCycle) {
			let shouldRestart = this.running;

			this.stop();
			this.cycleNext(shouldRestart ? this.start.bind(this) : null);
		}
	}

	/**
	 * Cycles to the previous element if possible.
	 * Used for the 'previous' control.
	 */
	previous(): void {
		if (this.userCanCycle) {
			let shouldRestart = this.running;

			this.stop();
			this.cyclePrevious(shouldRestart ? this.start.bind(this) : null);
		}
	}

	/**
	 * Starts the slideshow.
	 */
	start() {
		let slides = this.getSlides();

		this.cssAnimateSpeed = this.animateSpeed / 1000 + "s";
		this.ensureSlides();

		this.running = true;
		this.timeoutID = setInterval(this.cycleNext.bind(this), this.cycleSpeed);
	}

	/**
	 * Stops the slideshow.
	 */
	stop() {
		if (this.timeoutID) {
			clearInterval(this.timeoutID);
		}

		this.running = false;
	}
}

export = Slideshow;